import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";

const QuoteContext = createContext(null);

const STORAGE_KEY = "magit_quote_items";

export const QuoteProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product) => {
    if (itemsRef.current.find((p) => p.id === product.id)) {
      toast.info("Prodotto già nella lista preventivo.");
      return;
    }
    setItems((prev) =>
      prev.find((p) => p.id === product.id)
        ? prev
        : [
            ...prev,
            {
              id: product.id,
              code: product.code,
              name: product.name,
              category: product.category,
              image: product.image,
              quantity: 1,
            },
          ]
    );
    toast.success("Prodotto aggiunto alla lista preventivi!");
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
    toast("Prodotto rimosso dal preventivo.");
  }, []);

  const updateQuantity = useCallback((id, delta) => {
    setItems((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, quantity: Math.max(1, p.quantity + delta) } : p
      )
    );
  }, []);

  const clearItems = useCallback(() => setItems([]), []);
  const openDialog = useCallback(() => setIsOpen(true), []);
  const closeDialog = useCallback(() => setIsOpen(false), []);

  const requestQuote = useCallback(
    (product) => {
      addItem(product);
      setIsOpen(true);
    },
    [addItem]
  );

  return (
    <QuoteContext.Provider
      value={{
        items,
        count: items.length,
        addItem,
        removeItem,
        updateQuantity,
        clearItems,
        isOpen,
        openDialog,
        closeDialog,
        requestQuote,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};

export const useQuote = () => {
  const ctx = useContext(QuoteContext);
  if (!ctx) throw new Error("useQuote must be used within QuoteProvider");
  return ctx;
};
